import { useState, useEffect, useRef } from "react";
import { Settings2, GripVertical } from "lucide-react";
import { Button } from "../../components/ui/button";
import { ALL_COLUMNS, ALL_COL_KEYS, VISIBLE_COLS_KEY } from "./helpers";

export function ColumnVisibilityDropdown({ visibleCols, onChange, onReset }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const hiddenCols = ALL_COL_KEYS.filter((k) => !visibleCols.includes(k));
  const orderedAll = [...visibleCols, ...hiddenCols];

  const toggle = (key) => {
    const col = ALL_COLUMNS.find((c) => c.key === key);
    if (!col || !col.removable) return;
    let next;
    if (visibleCols.includes(key)) {
      next = visibleCols.filter((k) => k !== key);
    } else {
      next = [...visibleCols, key];
    }
    onChange(next);
    localStorage.setItem(VISIBLE_COLS_KEY, JSON.stringify(next));
  };

  const handleDragStart = (key) => { dragItem.current = key; };
  const handleDragOver = (e, key) => { e.preventDefault(); dragOverItem.current = key; };
  const handleDrop = (e) => {
    e.preventDefault();
    const from = dragItem.current;
    const to = dragOverItem.current;
    if (!from || !to || from === to) return;
    if (!visibleCols.includes(from) || !visibleCols.includes(to)) return;
    const arr = [...visibleCols];
    const fromIdx = arr.indexOf(from);
    const toIdx = arr.indexOf(to);
    arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, from);
    onChange(arr);
    localStorage.setItem(VISIBLE_COLS_KEY, JSON.stringify(arr));
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline"
        size="sm"
        className="h-7 px-2 text-[10px]"
        onClick={() => setOpen(!open)}
        data-testid="column-visibility-btn"
      >
        <Settings2 className="w-3.5 h-3.5" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1.5 min-w-[200px]" data-testid="column-visibility-dropdown">
          <p className="px-3 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Spalten (ziehen zum Sortieren)</p>
          {orderedAll.map((key) => {
            const col = ALL_COLUMNS.find((c) => c.key === key);
            if (!col) return null;
            const isVisible = visibleCols.includes(key);
            const isDraggable = isVisible && col.removable;
            return (
              <div
                key={key}
                draggable={isDraggable}
                onDragStart={() => handleDragStart(key)}
                onDragOver={(e) => handleDragOver(e, key)}
                onDrop={handleDrop}
                className={`flex items-center gap-1.5 px-2 py-1 text-[11px] hover:bg-gray-50 transition-colors ${
                  !isVisible ? "opacity-50" : ""
                }`}
                data-testid={`col-toggle-${key}`}
              >
                {isDraggable ? (
                  <GripVertical className="w-3 h-3 text-gray-300 cursor-grab flex-shrink-0" />
                ) : (
                  <div className="w-3 h-3 flex-shrink-0" />
                )}
                <label className={`flex items-center gap-2 flex-1 cursor-pointer ${!col.removable ? "cursor-default" : ""}`}>
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={() => toggle(key)}
                    disabled={!col.removable}
                    className="rounded border-gray-300 text-blue-600 w-3.5 h-3.5"
                  />
                  <span className="text-gray-700">{col.label}</span>
                </label>
                {!col.removable && <span className="text-[9px] text-gray-400 ml-auto">Pflicht</span>}
              </div>
            );
          })}
          <div className="border-t border-gray-100 mt-1 pt-1 px-2">
            <button
              onClick={() => { if (onReset) onReset(); }}
              className="w-full text-left px-1 py-1 text-[10px] text-gray-500 hover:text-gray-800 transition-colors"
              data-testid="col-reset-btn"
            >
              Zurücksetzen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
